import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, Input, Button, Checkbox, Form, message } from 'antd';
import { UserOutlined, LockOutlined, EyeOutlined, EyeInvisibleOutlined } from '@ant-design/icons';
import LanguageSwitcher from '../components/LanguageSwitcher';

const API_BASE_URL = (() => {
  const hostname = window.location.hostname;
  const port = window.location.port;
  if (!port || port === '80' || port === '443') {
    return `${window.location.protocol}//${hostname}/api/v1`;
  }
  if (port === '3000') {
    return `http://${hostname}:8000/api/v1`;
  }
  return `http://${hostname}:${port}/api/v1`;
})();

function LoginPage({ onLoginSuccess, onSwitchToRegister }) {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [form] = Form.useForm();

  useEffect(() => {
    const savedUser = localStorage.getItem('spo_remember_user');
    const savedPass = localStorage.getItem('spo_remember_pass');
    if (savedUser) {
      form.setFieldsValue({
        username: savedUser,
        password: savedPass || '',
        remember: true,
      });
    }
  }, [form]);

  const handleLogin = async (values) => {
    setLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          username: values.username,
          password: values.password,
        }),
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({ detail: response.statusText }));
        throw new Error(error.detail || `Login failed (HTTP ${response.status})`);
      }

      const data = await response.json();
      localStorage.setItem('spo_auth_token', data.token);

      if (values.remember) {
        localStorage.setItem('spo_remember_user', values.username);
        localStorage.setItem('spo_remember_pass', values.password);
      } else {
        localStorage.removeItem('spo_remember_user');
        localStorage.removeItem('spo_remember_pass');
      }

      message.success(t('login.success'));
      onLoginSuccess(data);
    } catch (error) {
      message.error(error.message || t('login.error'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      background: 'linear-gradient(135deg, #001529 0%, #003a8c 50%, #0050b3 100%)',
    }}>
      <Card
        style={{
          width: 400,
          borderRadius: 12,
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
          border: 'none',
        }}
        bodyStyle={{ padding: '40px 32px 32px' }}
      >
        <div style={{ textAlign: 'center', marginBottom: 32 }}>
          <a href="https://marcom.comboware.ai/" style={{ display: 'block' }}>
            <img
              src="/marcom-ai/marcom-ai-horizontal.svg"
              alt="marcom AI 行銷平台"
              style={{ width: '100%', maxWidth: 360, height: 'auto', display: 'block', margin: '0 auto' }}
            />
          </a>
        </div>

        <Form
          form={form}
          onFinish={handleLogin}
          initialValues={{ remember: false }}
          size="large"
        >
          <Form.Item
            name="username"
            rules={[{ required: true, message: t('login.usernameRequired') }]}
          >
            <Input
              prefix={<UserOutlined style={{ color: '#bfbfbf' }} />}
              placeholder={t('login.usernamePlaceholder')}
            />
          </Form.Item>

          <Form.Item
            name="password"
            rules={[{ required: true, message: t('login.passwordRequired') }]}
          >
            <Input
              prefix={<LockOutlined style={{ color: '#bfbfbf' }} />}
              type={showPassword ? 'text' : 'password'}
              placeholder={t('login.passwordPlaceholder')}
              suffix={
                <span
                  onClick={() => setShowPassword(!showPassword)}
                  style={{ cursor: 'pointer', color: '#bfbfbf' }}
                >
                  {showPassword ? <EyeInvisibleOutlined /> : <EyeOutlined />}
                </span>
              }
            />
          </Form.Item>

          <Form.Item name="remember" valuePropName="checked">
            <Checkbox>{t('login.rememberMe')}</Checkbox>
          </Form.Item>

          <Form.Item>
            <Button
              type="primary"
              htmlType="submit"
              loading={loading}
              block
              style={{
                height: 44,
                borderRadius: 8,
                fontWeight: 600,
                fontSize: 16,
                background: 'linear-gradient(135deg, #1890ff, #096dd9)',
                border: 'none',
              }}
            >
              {t('login.submit')}
            </Button>
          </Form.Item>
          {onSwitchToRegister && (
            <div style={{ textAlign: 'center' }}>
              <span style={{ color: '#999' }}>{t('login.noAccount')}</span>{' '}
              <Button type="link" onClick={onSwitchToRegister} style={{ padding: 0 }}>
                {t('login.register')}
              </Button>
            </div>
          )}
        </Form>
        <div style={{ textAlign: 'center', marginTop: 16 }}>
          <a
            href="https://marcom.comboware.ai/"
            style={{ color: '#8c8c8c', fontSize: 13 }}
          >
            {t('login.backToHome')}
          </a>
        </div>
        <div style={{ textAlign: 'center', marginTop: 12 }}>
          <LanguageSwitcher size="small" />
        </div>
      </Card>
    </div>
  );
}

export default LoginPage;
